import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toPng } from "html-to-image";
import QRCode from "react-qr-code";
import { toast } from "sonner";
import { X, Download, Link2, Share2 } from "lucide-react";
import { BRAND } from "../config/content";

const CardFace = ({ fragrance, shareUrl, innerRef }) => (
  <div
    ref={innerRef}
    className="w-[300px] aspect-[9/16] bg-ivory text-ink flex flex-col items-center justify-between px-7 py-9 border border-gold/60"
    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
  >
    <div className="flex flex-col items-center">
      <img src="/assets/logo-ink.png" alt="Lacrime di Coccodrillo" className="h-14 w-auto" />
    </div>

    <div className="flex flex-col items-center text-center">
      <span className="font-mono text-[8px] tracking-[0.3em] uppercase text-gold">
        Il mio profumo oggi è
      </span>
      <span
        className="mt-4 font-serif leading-[1.08]"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: fragrance.name.length > 12 ? "1.7rem" : "2.3rem",
        }}
      >
        {fragrance.name}
      </span>
      <span className="mt-4 h-px w-10 bg-gold" />
      <span className="mt-4 font-serif italic text-lg" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        E il tuo?
      </span>
    </div>

    <div className="flex flex-col items-center gap-3">
      <div className="bg-ivory p-2 border border-line">
        <QRCode value={shareUrl} size={64} fgColor="#141412" bgColor="#FBF9F4" />
      </div>
      <span className="font-serif italic text-[11px] text-muted2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        {BRAND.payoff}
      </span>
    </div>
  </div>
);

export const ShareCard = ({ fragrance, open, onClose }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const shareUrl = `${window.location.origin}?source=condivisione`;

  const renderPng = async () => {
    const dataUrl = await toPng(cardRef.current, { pixelRatio: 3, cacheBust: true });
    const blob = await (await fetch(dataUrl)).blob();
    return { dataUrl, blob };
  };

  const saveImage = async () => {
    setBusy(true);
    try {
      const { dataUrl } = await renderPng();
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `il-mio-profumo-${fragrance.id}.png`;
      a.click();
      toast.success("Immagine salvata.");
    } catch {
      toast.error("Non riesco a generare l’immagine.");
    } finally {
      setBusy(false);
    }
  };

  const shareNative = async () => {
    setBusy(true);
    try {
      const { blob } = await renderPng();
      const file = new File([blob], `il-mio-profumo-${fragrance.id}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: BRAND.name,
          text: `Il mio profumo oggi è ${fragrance.name}. E il tuo?`,
        });
      } else {
        await saveImage();
        toast.message("Condivisione diretta non disponibile: ho salvato l’immagine.");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Condivisione non riuscita.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiato.");
    } catch {
      toast.error("Non riesco a copiare il link.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="share-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-ink/90 overflow-y-auto"
        >
          <div className="min-h-full flex flex-col items-center px-6 py-10">
            <button
              onClick={onClose}
              data-testid="share-close-button"
              className="self-end text-ivory/70 hover:text-ivory transition-colors"
              aria-label="Chiudi"
            >
              <X className="w-6 h-6" />
            </button>

            <motion.div
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6"
            >
              <CardFace fragrance={fragrance} shareUrl={shareUrl} innerRef={cardRef} />
            </motion.div>

            <div className="mt-8 w-full max-w-[300px] space-y-3 pb-8">
              <button
                onClick={shareNative}
                disabled={busy}
                data-testid="share-native-button"
                className="flex w-full items-center justify-between bg-olive px-5 py-4 text-ivory transition-colors hover:bg-olive-dark disabled:opacity-50"
              >
                <span className="font-mono text-[11px] tracking-[0.25em]">CONDIVIDI</span>
                <Share2 className="w-4 h-4" />
              </button>
              <button
                onClick={saveImage}
                disabled={busy}
                data-testid="share-download-button"
                className="flex w-full items-center justify-between border border-ivory/30 px-5 py-4 text-ivory transition-colors hover:bg-ivory hover:text-ink disabled:opacity-50"
              >
                <span className="font-mono text-[11px] tracking-[0.25em]">SALVA L’IMMAGINE</span>
                <Download className="w-4 h-4" />
              </button>
              <button
                onClick={copyLink}
                data-testid="share-copy-link-button"
                className="flex w-full items-center justify-between px-5 py-4 text-ivory/70 transition-colors hover:text-ivory"
              >
                <span className="font-mono text-[11px] tracking-[0.25em]">COPIA IL LINK</span>
                <Link2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
